import { Link, useLocation, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Scissors,
  UserCircle,
  BarChart3,
  Settings,
  Clock3,
  Menu,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { SchedoraLogo } from "@/components/schedora/SchedoraLogo";
import { useApp } from "@/context/AppContext";

const navItems = [
  { to: "/owner/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/owner/calendar", icon: Calendar, label: "Calendar" },
  { to: "/owner/staff", icon: Users, label: "Staff" },
  { to: "/owner/clock-in", icon: Clock3, label: "Clock In" },
  { to: "/owner/services", icon: Scissors, label: "Services" },
  { to: "/owner/clients", icon: UserCircle, label: "Clients" },
  { to: "/owner/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/owner/settings", icon: Settings, label: "Settings" },
];

export default function OwnerLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentBusiness, currentUser, logout } = useApp();
  const initials = (currentBusiness?.name ?? currentUser?.displayName ?? "GS")
    .split(" ")
    .map((segment) => segment[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-background">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-60 border-r bg-card flex flex-col transition-transform lg:relative lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-14 items-center gap-2 px-4 border-b">
          <SchedoraLogo iconClassName="h-5 w-5" />
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                location.pathname === item.to
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="p-3 border-t">
          <Link
            to="/login"
            onClick={logout}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </Link>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b bg-background/80 backdrop-blur-md px-4 lg:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
              {initials}
            </div>
            <span className="hidden sm:inline text-sm font-medium text-foreground">{currentBusiness?.name ?? "Business"}</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
